import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9on6j0senbniolesi4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <main className="min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]} />

        <section className="mt-6 text-center max-w-3xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-heading text-foreground-950">关于我们</h1>
          <p className="text-sm text-foreground-500 mt-3">{aboutInfo.slogan}</p>
          <p className="text-sm text-foreground-600 mt-6 leading-relaxed">
            {aboutInfo.description}
          </p>
        </section>

        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center">我们的优势</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {aboutInfo.features.map((feature, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-6 text-center transition-all duration-300 hover:scale-[1.02]">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className={`${feature.icon} text-xl text-accent-600`}></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950">{feature.title}</h3>
                <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center">平台数据</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {aboutInfo.stats.map((stat, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-6 text-center">
                <span className="text-3xl font-heading text-accent-500">{stat.value}</span>
                <p className="text-xs text-foreground-500 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center">用户口碑</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {aboutInfo.testimonials.map((t, i) => (
              <div key={i} className="bg-background-100 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">&ldquo;{t.content}&rdquo;</p>
                <div className="flex items-center gap-0.5 mt-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 mb-10">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mt-2">有任何问题或建议？欢迎随时联系我们</p>

          <div className="flex flex-col lg:flex-row gap-8 mt-8 max-w-4xl mx-auto">
            <div className="lg:w-1/2 bg-background-100 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">联系方式</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100">
                    <i className="ri-wechat-line text-secondary-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.weixin}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-weibo-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">官方微博</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200">
                    <i className="ri-time-line text-foreground-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">工作时间</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.workingHours}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-1/2">
              <form onSubmit={handleSubmit} data-readdy-form className="bg-background-100 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-foreground-950 mb-4">发送消息</h3>

                <div className="space-y-3">
                  <div>
                    <label htmlFor="about-name" className="block text-xs text-foreground-600 mb-1">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs text-foreground-600 mb-1">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-xs text-foreground-600 mb-1">主题</label>
                    <input
                      id="about-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs text-foreground-600 mb-1">消息</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                      placeholder="请输入您的消息（最多500字符）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-secondary-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "关于我们 - 香蕉文化漫画网",
            "url": "https://www.xiangjiaowenhua.com/about",
            "description": aboutInfo.description,
            "isPartOf": {
              "@type": "WebSite",
              "name": "香蕉文化漫画网"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "香蕉文化漫画网有哪些阅读模式？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "香蕉文化漫画网提供下拉式、翻页和弹窗三种阅读模式，满足不同读者的阅读习惯。下拉式适合快速浏览，翻页模式还原纸质书体验，弹窗模式适合在手机上阅读。"
                }
              },
              {
                "@type": "Question",
                "name": "香蕉文化漫画网是否免费？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "是的，香蕉文化漫画网提供大量免费全集漫画，包括热门韩漫、日漫和国漫资源。部分漫画的前几话可以免费阅读，VIP章节需要会员权限。"
                }
              },
              {
                "@type": "Question",
                "name": "网站提供哪些类型的漫画？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "香蕉文化漫画网汇聚海量精品漫画，涵盖韩漫、日漫、国漫三大类型，并细分为恋爱、热血、奇幻、搞笑等多个题材分类，满足不同读者的阅读需求。"
                }
              }
            ]
          })
        }}
      />
    </main>
  );
}